/**
 * Every subject you have, and the way in to each one.
 *
 * This is the ProjectsListView that used to be level one of a four-level
 * drill-down (projects → categories → topics → keywords). The other three
 * levels are gone: `/subjects/:id` shows the whole tree in a rail and there is
 * nothing left to walk down through.
 *
 * Kept rather than rewritten — the list itself was never the problem. What was
 * wrong is that it was a corridor.
 */
import { useState, useEffect, FormEvent, useCallback } from 'react';
import PageHeader from '../components/common/PageHeader';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Modal from '../components/Modal';
import { useProject } from '../contexts/ProjectContext';
import GrowthAgentPanel from '../components/GrowthAgentPanel';
import type { Project } from '../types';

interface ProjectFormData {
  name: string;
  description: string;
  is_active: boolean;
}

export default function SubjectsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshProjects } = useProject();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<ProjectFormData>({ name: '', description: '', is_active: true });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await api.getProjects();
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Open modal if triggered from breadcrumb navigation

  // Arriving with ?create=true opens the modal, which is how the old
  // "+ New Subject" links behaved before the drill-down was retired.
  useEffect(() => {
    if (searchParams.get('create') !== 'true' || loading) return;
    setIsModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [searchParams, loading, setSearchParams]);

  const openCreateModal = () => {
    setFormData({ name: '', description: '', is_active: true });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      await api.createProject(formData);
      setIsModalOpen(false);
      fetchProjects();
      refreshProjects();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create subject');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>;

  return (
    <div>
      {/* SAY WHAT IT IS FOR, not just what it is. "Something you cover" was
          accurate and abstract, and abstract was the complaint: people could not
          tell what making one would get them. Under a shell called Publish the
          answer is available and should be said — a subject is what you publish
          ABOUT, and pages come out of it. */}
      <PageHeader
        title="Subjects"
        subtitle="A subject is what you publish about - an entire site, a section of one, or a focused campaign. Pages come out of it."
        className="mb-4"
        actions={
          <button onClick={openCreateModal} className="bg-blue-600 text-white px-3 py-2 sm:px-4 rounded-md hover:bg-blue-700 text-sm">
            + New Subject
          </button>
        }
      />

      {error && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">{error}</div>}

      {projects.length === 0 ? (
        /* Two ways in, because they suit different starts. A bare subject
           is right when you already know what it is about; more often you
           do not yet, and attaching something you found is what brings one
           into existence — which is the model the whole rework is built on.
           Offering only "create" sends a new user to an empty workspace. */
        <div className="text-center py-10 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-600 dark:text-gray-300">No subjects yet.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Pick something you want to publish about. The research you gather and
            the pages you write from it both live here.
          </p>
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              onClick={openCreateModal}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              New Subject
            </button>
            <Link
              to="/trending"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              or find something in Trends first
            </Link>
          </div>
        </div>
      ) : (
        /* CARDS, not rows. A row is a corridor: it reads as one line of a list you
           scan top to bottom, and the only thing it can hold beyond the name is a
           chip or two before it runs out of width — which is why the subject's
           description was truncated to a single line and the row ended in a chevron
           whose whole job was to say "this goes somewhere".

           A card gives the description two lines and the counts a row of their own,
           and three across means a dozen subjects fit on one screen instead of
           scrolling past a dozen identical bars. */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <Link
              key={project.id}
              to={`/subjects/${project.id}`}
              className="flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">
                  {project.name}
                </h3>
                {/* Still one click to flip, from the card that shows it. The card IS
                    a link now, so the click has to be stopped from following it —
                    `stopPropagation` alone was enough on a div's onClick handler and
                    is not enough here. */}
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await api.updateProject(project.id, { is_active: !project.is_active });
                    setProjects((prev) => prev.map((p) => p.id === project.id ? { ...p, is_active: !p.is_active } : p));
                  }}
                  className={`shrink-0 px-2 py-0.5 text-xs font-medium rounded cursor-pointer hover:opacity-80 ${project.is_active ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}
                >
                  {project.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>

              {project.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{project.description}</p>
              )}

              <GrowthAgentPanel projectId={project.id} statusOnly />

              {/* `mt-auto` in a flex column, so the footers line up across a row
                  whatever length the descriptions are. */}
              <div className="mt-auto pt-3 flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                <span>{project.category_count} {project.category_count === 1 ? 'section' : 'sections'}</span>
                <span>·</span>
                <span>{new Date(project.updated_at).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="New Subject">
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">{formError}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ============ Project Detail View ============
interface ProjectDetailViewProps {
  projectId: number;
  onBack: () => void;
  onSelectCategory: (categoryId: number) => void;
  openCreateModal?: boolean;
  onCreateModalOpened?: () => void;
}
